import React, {
  useContext, useEffect, useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import { Badge, PaymentReceiveIcon, ProfileIcon, SignOutIcon, VehicleDriverSideIcon } from '@motorway/mw-highway-code';

import cypressIds from 'CypressId';

import { UserContext } from 'Context/user';
import { useRemoveUser } from 'Context/useRemoveUser';

import { SP_HEADER_EVENTS } from 'Utilities/analytics/analyticsHeader';
import { applyCypressData } from 'Utilities/index';

import { useBreakpoint } from '../../../components/misc/Breakpoints/Breakpoints';
import { getReferBannerLink } from '../checkerPages/Pages/CheckerRouter.utils';
import { AlwaysOnExperiment } from '../experiments/useAlwaysOnExperiment/AlwaysOnExperiment';

import { handleLogoutClick } from './AccountLinks.helpers';
import LocalTexts from './HeaderTexts.json';

import styles from './Header.scss';

const LocalT = new MDText(LocalTexts);
const t = (key: string, props?: { [key:string]: unknown }) => LocalT.translate(key, props) as string;

export const texts = {
  account: t('navigation.accountLabel'),
  refer: t('navigation.referLabel'),
  referValue: t('navigation.referValue'),
  signIn: t('navigation.signInLabel'),
  signOut: t('navigation.signOutLabel'),
};

const CSS_IDS = Object.freeze({
  ACCOUNT_TOGGLE: 'header-account-toggle',
  NAV_MOBILE: 'userNavMobile',
});

export const PATHS = {
  ACCOUNT: '/account',
  REFER_DESKTOP: getReferBannerLink('desktop_navigation'),
  SIGN_IN: '/sign-in',
};

const ConstantAccountLinks = () => {
  const { removeUser } = useRemoveUser();

  const referLink = PATHS.REFER_DESKTOP;

  const accountOnClick = () => {
    SP_HEADER_EVENTS.HEADER_LINK_CLICKED({
      label: 'from_hover',
      name: 'account',
      url: PATHS.ACCOUNT,
    });
  };

  const referOnClick = () => {
    SP_HEADER_EVENTS.HEADER_LINK_CLICKED({
      label: 'desktop_navigation',
      name: 'refer',
      url: referLink,
    });
  };

  const handleOnLogOutClick = (e: React.MouseEvent) => {
    handleLogoutClick(e);
    removeUser();
  };

  return (
    <ul>
      <li className={styles.navItem}>
        <Link
          onClick={accountOnClick}
          to={PATHS.ACCOUNT}
        >
          <VehicleDriverSideIcon />
          { texts.account }
        </Link>
      </li>
      <li className={styles.navItem}>
        <Link
          onClick={referOnClick}
          to={referLink}
        >
          <PaymentReceiveIcon />
          { texts.refer }
          <span className={styles.referBadge}>
            <Badge text={texts.referValue} variant='success' />
          </span>
        </Link>
      </li>
      <li className={styles.navItem}>
        <a href='#signout' onClick={handleOnLogOutClick}>
          <SignOutIcon />
          { texts.signOut }
        </a>
      </li>
    </ul>
  );
};

export const AccountLinks = () => {
  let initials;

  const breakpoints = useBreakpoint();
  const isMobile = breakpoints.maxWidth.breakpointTablet;

  const [menuOpen, setMenuOpen] = useState(false);

  const menuElem = useRef<HTMLDivElement>(null);

  const { userState: user } = useContext(UserContext);
  const isUser = Number.isInteger(user?.id);

  if (isUser && user?.name) {
    initials = user.name.charAt(0);
    if (user.name.split(' ').length > 1) {
      initials += user.name.split(' ')[1].charAt(0);
    }
    initials = initials.toUpperCase();
  }

  const toggleMenu = () => {
    if (!isMobile) {
      SP_HEADER_EVENTS.HEADER_LINK_CLICKED({ label: 'direct', name: 'account', url: PATHS.ACCOUNT });
    }
  };

  useEffect(() => {
    if (!isMobile) {
      setMenuOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (menuOpen) {
      window.scrollTo(0, 0);
    }
  }, [menuOpen]);

  useEffect(() => {
    if (menuElem.current && !menuOpen) {
      menuElem.current.scrollTop = 0;
    }
  }, [menuElem, menuOpen]);

  return (
    <div className={cx(styles.account, styles.toggle)}>
      {isUser ? (
        <>
          <AlwaysOnExperiment />
          <Link
            className={styles.user}
            {...applyCypressData(cypressIds.links.accountLink)}
            id={CSS_IDS.ACCOUNT_TOGGLE}
            onClick={toggleMenu}
            onMouseEnter={() => SP_HEADER_EVENTS.HEADER_LINK_HOVERED({ message: 'Account menu', name: 'account' })}
            to={PATHS.ACCOUNT}
          >
            <div className={styles.initials}>{initials}</div>
            <div className={styles.name}>{user?.name?.split(' ')[0]}</div>
          </Link>
          <div className={cx(styles.nav, styles.navDesktop, styles.subMenu)}>
            <ConstantAccountLinks />
          </div>

          <div
            ref={menuElem}
            className={cx(styles.nav, styles.navMobile)}
            id={CSS_IDS.NAV_MOBILE}
            {...applyCypressData(cypressIds.sections.mobileNav)}
          >
            <ConstantAccountLinks />
          </div>
        </>
      ) : (
        <Link
          aria-label={texts.signIn}
          className={styles.signIn}
          data-cy='signInLink'
          onClick={() => SP_HEADER_EVENTS.HEADER_LINK_CLICKED({ name: 'sign_in', url: PATHS.SIGN_IN })}
          to={PATHS.SIGN_IN}
        >
          <ProfileIcon filled={false} />
          <span className='signInLabel'>
            { texts.signIn }
          </span>
        </Link>
      )}
    </div>
  );
};
